import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ROUTE_PATHS } from 'core/constants/routePath'
import type { ProfileListRow } from 'lauria/pages/profiles/profilesListRow.types'
import { TruncatedCell } from 'lauria/components/profiles/TruncatedCell'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router'
import { ReactComponent as UserIcon } from 'core/assets/images/icones/userIcon.svg'
import { ReactComponent as ApiIcon } from 'core/assets/images/icones/apiIcon.svg'

export const ProfilesListMobile: React.FC<{
  users: ProfileListRow[]
}> = ({ users }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const editProfile = (idProfile: string) => {
    navigate(generatePath(ROUTE_PATHS.PROFILES_EDIT, { idProfile }))
  }
  const columnHelper = createColumnHelper<ProfileListRow>()
  const columns = [
    columnHelper.accessor('nomUtilisateur', {
      header: t('profiles.nomUtilisateur'),
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-2">
          <span
            aria-hidden="true"
            className="flex items-center justify-center w-[20px] h-[20px] shrink-0"
          >
            {row.original.compteService ? (
              <ApiIcon className="w-[20px] h-[20px] block" />
            ) : (
              <UserIcon className="w-[20px] h-[20px] block" />
            )}
          </span>
          <TruncatedCell value={row.original.nomUtilisateur} />
        </span>
      ),
    }),
    columnHelper.accessor('nom', {
      header: t('profiles.nom'),
      cell: ({ row }) => (
        <TruncatedCell value={`${row.original.nom} ${row.original.prenom}`} />
      ),
    }),
    columnHelper.accessor('email', {
      header: t('profiles.email'),
      cell: ({ getValue }) => <TruncatedCell value={getValue()} />,
    }),
    columnHelper.accessor('tagsUtilisateur', {
      header: t('profiles.tagsUtilisateur'),
      cell: ({ getValue }) => {
        const tags = getValue()
        return tags.length > 0 ? (
          <div className="tags tags-wrap">
            {tags.map((tag, index) => (
              <span key={index} className="tag">
                {tag}
              </span>
            ))}
          </div>
        ) : (
          '-'
        )
      },
    }),
    columnHelper.accessor('tagsAdministrateur', {
      header: t('profiles.tagsAdministrateur'),
      cell: ({ getValue }) => {
        const tags = getValue()
        return tags.length > 0 ? (
          <div className="tags tags-wrap">
            {tags.map((tag, index) => (
              <span key={index} className="tag">
                {tag}
              </span>
            ))}
          </div>
        ) : (
          '-'
        )
      },
    }),
  ]
  const table = useReactTable({
    data: users,
    columns,
    initialState: {},
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <>
      {users.length === 0 ? (
        <div className="w-full py-10 text-center text-gray-600">
          {t('profiles.noResult')}
        </div>
      ) : (
        <table className="table-profile">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="hover:bg-gray-50"
                onClick={() => editProfile(row.original.idProfile)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}
